import { z } from 'zod';
import { CurrencyConfig } from '../finance/currency-schema';
import { PaymentMethodSchema } from '../finance/payment-method-schema';
import { CatalogSectionSchema } from './catalog-base-schema';
import {
  ProductLayoutSchema,
  type ProductLayout,
} from './product-layout-schema';

export const AvailabilityDaySchema = z.object({
  enabled: z.boolean().default(false),
  blocks: z
    .array(
      z.object({
        start: z.string(),
        end: z.string(),
      }),
    )
    .default([]),
});

export const DEFAULT_AVAILABILITY = {
  monday: { enabled: true, blocks: [{ start: '09:00', end: '17:00' }] },
  tuesday: { enabled: true, blocks: [{ start: '09:00', end: '17:00' }] },
  wednesday: { enabled: true, blocks: [{ start: '09:00', end: '17:00' }] },
  thursday: { enabled: true, blocks: [{ start: '09:00', end: '17:00' }] },
  friday: { enabled: true, blocks: [{ start: '09:00', end: '17:00' }] },
  saturday: { enabled: false, blocks: [] },
  sunday: { enabled: false, blocks: [] },
};

export const AvailabilitySchema = z.object({
  monday: AvailabilityDaySchema.default(DEFAULT_AVAILABILITY.monday),
  tuesday: AvailabilityDaySchema.default(DEFAULT_AVAILABILITY.tuesday),
  wednesday: AvailabilityDaySchema.default(DEFAULT_AVAILABILITY.wednesday),
  thursday: AvailabilityDaySchema.default(DEFAULT_AVAILABILITY.thursday),
  friday: AvailabilityDaySchema.default(DEFAULT_AVAILABILITY.friday),
  saturday: AvailabilityDaySchema.default(DEFAULT_AVAILABILITY.saturday),
  sunday: AvailabilityDaySchema.default(DEFAULT_AVAILABILITY.sunday),
});

// Re-export for backward compatibility
export { CatalogSectionSchema, ProductLayoutSchema, type ProductLayout };

// Service Group Schema - for organizing products into categories
export const ServiceGroupSchema = z.object({
  id: z.string(),
  parentId: z.string().nullable().optional(), // Added for nested categories
  name: z.string().min(1, 'El nombre es obligatorio'),
  order: z.number().default(0),
  defaultLayout: ProductLayoutSchema.optional(),
  slug: z.string().optional(),
  image: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  showInHome: z.boolean().default(true),
  homeDisplayMode: z.enum(['inline', 'button']).default('button'),
  buttonText: z.string().nullable().optional(),
});
export type ServiceGroup = z.infer<typeof ServiceGroupSchema>;

// Definimos FinanceSchema por separado para poder inferir su tipo limpiamente
export const FinanceSchema = z.object({
  paymentMethods: z.array(PaymentMethodSchema).default([]),
  currencyConfig: CurrencyConfig,
});

export const PublicSchema = z.object({
  meta: z.object({
    ownerId: z.string(),
    accountType: z.enum(['professional', 'business']),
    sectionOrder: z
      .array(z.string())
      .default(['products', 'catalog', 'location', 'academy']),
    customTitles: z.record(z.string(), z.string()).optional().default({}),
    customBlocks: z
      .record(
        z.string(),
        z.object({
          type: z.enum(['link', 'text', 'banner']),
          title: z.string().optional(),
          url: z.string().optional(),
          content: z.string().optional(),
          isActive: z.boolean().default(true),
        }),
      )
      .optional()
      .default({}),
    customDomain: z.string().nullable().optional(),
    masterPixelId: z.string().nullable().optional(), // Administrado por Tufirma Admin
    viewMode: z.enum(['standard', 'catalog']).default('standard').optional(),
  }),

  branding: z.object({
    name: z.string(),
    profession: z.string(),
    slug: z.string().min(3),
    viewMode: z.enum(['standard', 'catalog']).default('standard').optional(),
    logoUrl: z.string().nullable(),
    bannerImage: z.string().nullable(),
    description: z.string().nullable(),
    isVerified: z.boolean().default(false),
    styles: z.object({
      theme: z.enum(['light', 'dark']).default('light'),
      primary: z.string().nullable().optional(),
      // 🎨 NUEVOS CONTROLES DE PERSONALIZACIÓN
      fontFamily: z.enum(['sans', 'serif', 'mono', 'rounded']).default('sans'),
      borderRadius: z.enum(['none', 'sm', 'md', 'lg', 'full']).default('lg'),
      heroStyle: z.enum(['minimal', 'split', 'impact']).default('minimal'),
      buttonStyle: z.enum(['solid', 'outline', 'soft']).default('solid'),
    }),
    showResuelveloFooter: z.boolean().default(true),
    contact: z.object({
      tiktok: z.string().nullable(),
      facebook: z.string().nullable(),
      phone: z.string().nullable(),
      heroWhatsappMessage: z.string().nullable().optional(),
      instagram: z.string().nullable(),
      google_maps: z.string().nullable(),
      address: z.string().nullable(),
    }),
  }),

  // Legacy scheduler surface. Kept only so older settings screens/builds do not break.
  availability: AvailabilitySchema.optional().default(DEFAULT_AVAILABILITY),

  finance: FinanceSchema.default({
    paymentMethods: [],
    currencyConfig: {
      accountingCurrency: 'USD',
      localCurrency: 'BS',
      multiCurrency: true,
      ratePolicy: 'official',
    },
  }),

  marketing: z
    .object({
      facebookPixelId: z.string().nullable().optional(),
      capiToken: z.string().nullable().optional(),
      googleAnalyticsId: z.string().nullable().optional(),
      tiktokPixelId: z.string().nullable().optional(),
    })
    .default({}),

  catalog: z.array(CatalogSectionSchema).default([]),
  productGroups: z.array(ServiceGroupSchema).default([]),

  // --- ACADEMY: PUBLIC SETTINGS ---
  academy: z
    .object({
      enabled: z.boolean().default(false),
      title: z.string().default('Academia Online'),
      description: z.string().optional(),
      featuredCourseIds: z.array(z.string()).default([]),
      ctaConfig: z
        .object({
          show: z.boolean().default(true),
          title: z.string().default('¿Listo para aprender?'),
          subtitle: z
            .string()
            .default('Explora nuestros cursos profesionales y masterclasses.'),
          buttonText: z.string().default('Ver Academia'),
        })
        .optional(),
    })
    .default({
      enabled: false,
      title: 'Academia Online',
      featuredCourseIds: [],
    }),
});

// --- EXPORTACIÓN DE TIPOS ---
export type Public = z.infer<typeof PublicSchema>;
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;
export type FinanceData = z.infer<typeof FinanceSchema>;
export type BradingForm = z.infer<typeof PublicSchema.shape.branding>;
export type BrandingDataWithoutSlug = Omit<BradingForm, 'slug'>;
